import { UUID } from "./outings";
import {
  MIN_PERSONAL_FEEDBACK_DAYS,
  PERSONAL_PREFERENCE_SIGNALS,
  type PersonalPreferenceReview,
} from "@/lib/planner/personal-preferences";
import {
  ROUTE_OUTCOME_REASONS,
  type RouteAgainAnswer,
  type RouteOutcomeReason,
} from "@/lib/planner/route-outcomes";
import type { AreaSport } from "@/lib/planner/graph-types";

export const MAX_OUTING_FEEDBACK = 200;
export const OUTING_FEEDBACK_BYTES = 131072;
export const FEEDBACK_REVIEW_MS = 120000;
export const OUTING_FEEDBACK_ACTIVITIES = ["ski", "snowboard", "mtb"] as const;
export type FeedbackActivity = (typeof OUTING_FEEDBACK_ACTIVITIES)[number];
export type OutingFeedbackEntry = {
  outingId: string;
  owner: string;
  revision: number;
  sourceRevision: number;
  activity: FeedbackActivity;
  localDate: string;
  reportedAt: string;
  answer: RouteAgainAnswer | null;
  reasons: RouteOutcomeReason[];
  deleted: boolean;
};
export type OutingFeedbackSettings = {
  revision: number;
  enabled: boolean;
  resetAt: string | null;
};
export type OutingFeedbackSnapshot = {
  owner: string;
  checkedAt: string;
  settings: OutingFeedbackSettings;
  entries: OutingFeedbackEntry[];
};
export type OutingFeedbackCommand = {
  owner: string;
  mutationId: string;
  outingId: string;
  expectedRevision: number;
  sourceRevision: number;
  answer: RouteAgainAnswer | null;
  reasons: RouteOutcomeReason[];
  operation: "save" | "delete";
};
export type FeedbackSettingsCommand = {
  owner: string;
  mutationId: string;
  expectedRevision: number;
  operation: "enable" | "disable" | "reset";
};
export type OutingFeedbackDetail = {
  owner: string;
  checkedAt: string;
  source: {
    outingId: string;
    revision: number;
    activity: FeedbackActivity;
    localDate: string;
  };
  entry: OutingFeedbackEntry | null;
};
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const keys = (
  value: unknown,
  names: readonly string[],
): value is Record<string, unknown> =>
  object(value) &&
  Object.keys(value).length === names.length &&
  names.every((name) => Object.hasOwn(value, name));
const revision = (value: unknown, minimum = 0): value is number =>
  Number.isSafeInteger(value) &&
  Number(value) >= minimum &&
  Number(value) <= 2147483646;
const uuid = (value: unknown): value is string =>
  typeof value === "string" && UUID.test(value);
const timestamp = (value: unknown): value is string =>
  typeof value === "string" &&
  /^20\d\d-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{1,6})?(?:Z|\+00:00)$/.test(value) &&
  date(value.slice(0, 10)) &&
  Number.isFinite(Date.parse(value));
const date = (value: unknown): value is string =>
  typeof value === "string" &&
  /^20\d\d-\d\d-\d\d$/.test(value) &&
  Number.isFinite(Date.parse(`${value}T12:00:00Z`)) &&
  new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;
const answers = (value: unknown): value is RouteAgainAnswer =>
  value === "yes" || value === "maybe" || value === "no";
function bounded(value: unknown): boolean {
  try {
    return (
      new TextEncoder().encode(JSON.stringify(value)).byteLength <=
      OUTING_FEEDBACK_BYTES
    );
  } catch {
    return false;
  }
}
function reasons(value: unknown): value is RouteOutcomeReason[] {
  return (
    Array.isArray(value) &&
    value.length <= 3 &&
    new Set(value).size === value.length &&
    value.every((reason) => ROUTE_OUTCOME_REASONS.some(([id]) => id === reason))
  );
}
export function validFeedbackEntry(
  value: unknown,
  owner: string,
): value is OutingFeedbackEntry {
  if (
    !keys(value, [
      "outingId",
      "owner",
      "revision",
      "sourceRevision",
      "activity",
      "localDate",
      "reportedAt",
      "answer",
      "reasons",
      "deleted",
    ])
  )
    return false;
  return (
    uuid(value.outingId) &&
    value.owner === owner &&
    uuid(owner) &&
    revision(value.revision, 1) &&
    revision(value.sourceRevision, 1) &&
    OUTING_FEEDBACK_ACTIVITIES.some(
      (activity) => activity === value.activity,
    ) &&
    date(value.localDate) &&
    timestamp(value.reportedAt) &&
    typeof value.deleted === "boolean" &&
    reasons(value.reasons) &&
    (value.deleted
      ? value.answer === null && value.reasons.length === 0
      : answers(value.answer))
  );
}
export function validFeedbackCommand(
  value: unknown,
): value is OutingFeedbackCommand {
  if (
    !keys(value, [
      "owner",
      "mutationId",
      "outingId",
      "expectedRevision",
      "sourceRevision",
      "answer",
      "reasons",
      "operation",
    ])
  )
    return false;
  return (
    uuid(value.owner) &&
    uuid(value.mutationId) &&
    uuid(value.outingId) &&
    revision(value.expectedRevision) &&
    revision(value.sourceRevision, 1) &&
    reasons(value.reasons) &&
    (value.operation === "delete"
      ? value.answer === null && value.reasons.length === 0
      : value.operation === "save" && answers(value.answer))
  );
}
export function validFeedbackSettingsCommand(
  value: unknown,
): value is FeedbackSettingsCommand {
  return (
    keys(value, ["owner", "mutationId", "expectedRevision", "operation"]) &&
    uuid(value.owner) &&
    uuid(value.mutationId) &&
    revision(value.expectedRevision) &&
    typeof value.operation === "string" &&
    ["enable", "disable", "reset"].includes(String(value.operation))
  );
}
export function readFeedbackSnapshot(
  value: unknown,
  owner: string,
  now = Date.now(),
): OutingFeedbackSnapshot | null {
  if (
    !keys(value, ["owner", "checkedAt", "settings", "entries"]) ||
    !uuid(owner) ||
    value.owner !== owner ||
    !timestamp(value.checkedAt) ||
    Date.parse(value.checkedAt) > now + 30000 ||
    now - Date.parse(value.checkedAt) > FEEDBACK_REVIEW_MS ||
    !keys(value.settings, ["revision", "enabled", "resetAt"]) ||
    !revision(value.settings.revision) ||
    typeof value.settings.enabled !== "boolean" ||
    (value.settings.resetAt !== null &&
      (!timestamp(value.settings.resetAt) ||
        Date.parse(value.settings.resetAt) > now + 30000)) ||
    !Array.isArray(value.entries) ||
    value.entries.length > MAX_OUTING_FEEDBACK ||
    !bounded(value)
  )
    return null;
  if (
    value.settings.revision === 0 &&
    (value.settings.enabled || value.settings.resetAt !== null)
  )
    return null;
  const ids = new Set<string>();
  for (const entry of value.entries) {
    if (
      !validFeedbackEntry(entry, owner) ||
      ids.has(entry.outingId) ||
      Date.parse(entry.reportedAt) > now + 30000
    )
      return null;
    ids.add(entry.outingId);
  }
  return value as unknown as OutingFeedbackSnapshot;
}

export function readFeedbackDetail(
  value: unknown,
  owner: string,
  outingId: string,
  now = Date.now(),
): OutingFeedbackDetail | null {
  if (
    !keys(value, ["owner", "checkedAt", "source", "entry"]) ||
    !uuid(owner) ||
    value.owner !== owner ||
    !timestamp(value.checkedAt) ||
    Date.parse(value.checkedAt) > now + 30000 ||
    now - Date.parse(value.checkedAt) > FEEDBACK_REVIEW_MS ||
    !keys(value.source, ["outingId", "revision", "activity", "localDate"]) ||
    !uuid(outingId) ||
    value.source.outingId !== outingId ||
    !revision(value.source.revision, 1) ||
    !OUTING_FEEDBACK_ACTIVITIES.includes(
      value.source.activity as FeedbackActivity,
    ) ||
    !date(value.source.localDate) ||
    (value.entry !== null &&
      (!validFeedbackEntry(value.entry, owner) ||
        value.entry.outingId !== outingId ||
        Date.parse(value.entry.reportedAt) > now + 30000)) ||
    !bounded(value)
  )
    return null;
  return value as unknown as OutingFeedbackDetail;
}

/** Excludes transport time; a fresh check must match the actual evidence reviewed. */
export function feedbackEvidenceKey(snapshot: OutingFeedbackSnapshot): string {
  return JSON.stringify([
    snapshot.owner,
    snapshot.settings.revision,
    snapshot.settings.enabled,
    snapshot.settings.resetAt,
    [...snapshot.entries]
      .sort((a, b) => a.outingId.localeCompare(b.outingId))
      .map((e) => [
        e.outingId,
        e.revision,
        e.sourceRevision,
        e.activity,
        e.localDate,
        e.reportedAt,
        e.answer,
        [...e.reasons].sort(),
        e.deleted,
      ]),
  ]);
}

/** Explicit owned answers only. Legacy browser-wide route feedback is never imported. */
export function reviewOutingFeedback(
  snapshot: OutingFeedbackSnapshot,
  sport: AreaSport,
  now = Date.now(),
): PersonalPreferenceReview {
  const empty: PersonalPreferenceReview = {
    sport,
    feedbackCount: 0,
    dayCount: 0,
    suggestions: [],
  };
  if (
    !readFeedbackSnapshot(snapshot, snapshot.owner, now) ||
    !snapshot.settings.enabled
  )
    return empty;
  const reset =
    snapshot.settings.resetAt === null
      ? -Infinity
      : Date.parse(snapshot.settings.resetAt);
  const oldest = now - 365 * 86400000;
  const entries = snapshot.entries.filter(
    (entry) =>
      !entry.deleted &&
      (sport === "mtb"
        ? entry.activity === "mtb"
        : entry.activity === "ski" || entry.activity === "snowboard") &&
      Date.parse(entry.reportedAt) > reset &&
      Date.parse(entry.reportedAt) >= oldest &&
      Date.parse(`${entry.localDate}T12:00:00Z`) >= oldest &&
      Date.parse(`${entry.localDate}T00:00:00Z`) <= now + 14 * 3600000,
  );
  const dayCount = new Set(entries.map((entry) => entry.localDate)).size;
  const suggestions =
    dayCount < MIN_PERSONAL_FEEDBACK_DAYS
      ? []
      : PERSONAL_PREFERENCE_SIGNALS.flatMap((signal) => {
          const supportingDays = new Set(
            entries
              .filter(
                (entry) =>
                  entry.reasons.includes(signal.reason) &&
                  (!signal.positive || entry.answer === "yes"),
              )
              .map((entry) => entry.localDate),
          ).size;
          return supportingDays >= 2 ? [{ ...signal, supportingDays }] : [];
        });
  return { sport, feedbackCount: entries.length, dayCount, suggestions };
}
